import { format } from 'date-fns'
import {
  CheckCircle2Icon,
  ChevronDownIcon,
  LightbulbIcon,
  MessageCircleIcon,
  WrenchIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import type { SubmissionKind } from '@/db/schema/enums'
import { isTickedCard } from '@/lib/coaching/submission-kind'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

const KIND_META: Record<
  string,
  { label: string; icon: typeof CheckCircle2Icon; chip: string }
> = {
  praise: {
    label: 'Praise',
    icon: CheckCircle2Icon,
    chip: 'bg-task-exercise/12 text-task-exercise',
  },
  critique: {
    label: 'Critique',
    icon: WrenchIcon,
    chip: 'bg-task-video/12 text-task-video',
  },
  suggestion: {
    label: 'Suggestion',
    icon: LightbulbIcon,
    chip: 'bg-task-article/12 text-task-article',
  },
}

// Annotated on `string` so the `??` below is a live branch — `review.kind`
// comes off the wire as a `string`, and a tab open across a deploy can hold a
// kind this bundle has never heard of — and `satisfies` on the literal so the
// enum still has to be covered.
const SUBMISSION_KIND_LABELS: Readonly<Record<string, string | undefined>> = {
  coding: 'Coding',
  system_design: 'System design',
  behavioral: 'Behavioral',
  // LG-905: the client ticked this card. They did not hand work in, so the
  // badge says what they did rather than naming a kind of work.
  manual: 'Marked done',
} satisfies Record<SubmissionKind, string>

/**
 * `Object.hasOwn`, not a bare `??`: `'__proto__'` and `'constructor'` resolve
 * up the prototype chain, so the fallback would never fire and React would be
 * handed an object as a child. `review.kind` is a `string` off the wire.
 */
function submissionKindLabel(kind: string): string {
  if (!Object.hasOwn(SUBMISSION_KIND_LABELS, kind)) return kind
  return SUBMISSION_KIND_LABELS[kind] ?? kind
}

/** Live coach feedback for this client, with unread state from receipts. */
export function useCoachingFeedback() {
  const query = trpc.dashboard.feedback.list.useQuery()
  const reviews = query.data?.reviews ?? []
  return {
    reviews,
    unreadCount: reviews.filter((review) => review.unread).length,
    isLoading: query.isLoading,
    // Surfaced so callers can distinguish "nothing to show" from "we could not
    // find out" — an empty list means the same thing in both states, and
    // telling a client their coach has not reviewed anything when the request
    // failed is worse than saying nothing.
    isError: query.isError,
  }
}

/** The kind chip for a review comment, shared by the panel and the card dialog. */
export function ReviewCommentKindChip({ kind }: { kind: string }) {
  const meta = KIND_META[kind]
  if (!meta) return null
  return (
    <span
      className={cn(
        'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
        meta.chip,
      )}
    >
      {meta.label}
    </span>
  )
}

export function CoachingFeedback() {
  const utils = trpc.useUtils()
  const { reviews, isLoading } = useCoachingFeedback()
  const markViewed = trpc.dashboard.feedback.markViewed.useMutation()
  const [openReviewId, setOpenReviewId] = useState<string | null>(null)

  const toggleReview = (reviewId: string, unread: boolean) => {
    const opening = openReviewId !== reviewId
    setOpenReviewId(opening ? reviewId : null)
    if (opening && unread) {
      // Opening feedback upserts the receipt; unread derives from later
      // publications, so a comment added after this visit re-flags it.
      void markViewed
        .mutateAsync({ reviewId })
        .then(() => utils.dashboard.feedback.list.invalidate())
        .catch(() => {})
    }
  }

  if (isLoading) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Loading feedback…
      </p>
    )
  }
  if (reviews.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-10 text-center">
        <MessageCircleIcon
          className="size-6 text-muted-foreground"
          aria-hidden="true"
        />
        <p className="text-sm font-medium">No coach feedback yet</p>
        <p className="text-sm text-muted-foreground">
          Feedback appears here after your coach reviews a submission.
        </p>
      </div>
    )
  }

  return (
    <section aria-label="Coach feedback" className="flex flex-col gap-3">
      {reviews.map((review) => {
        const open = openReviewId === review.reviewId
        return (
          <div key={review.reviewId} className="rounded-xl border">
            <button
              type="button"
              onClick={() => toggleReview(review.reviewId, review.unread)}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-accent/50 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              aria-expanded={open}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">
                    {review.assignmentTitle}
                  </span>
                  <Badge variant="secondary">
                    {submissionKindLabel(review.kind)}
                  </Badge>
                  {review.submissionNumber > 1 &&
                    !isTickedCard(review.kind) && (
                      <Badge variant="outline">
                        Attempt {review.submissionNumber}
                      </Badge>
                    )}
                  {review.unread && (
                    <Badge className="bg-destructive text-white">New</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {review.comments.length}{' '}
                  {review.comments.length === 1 ? 'comment' : 'comments'}
                  {review.reviewedAt
                    ? ` · reviewed ${format(new Date(review.reviewedAt), 'MMM d, yyyy')}`
                    : ' · review in progress'}
                </p>
              </div>
              <ChevronDownIcon
                className={cn(
                  'size-4 shrink-0 text-muted-foreground transition-transform',
                  open && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </button>
            {open && (
              <div className="flex flex-col gap-2.5 border-t px-4 py-3">
                {review.comments.map((comment) => {
                  const meta = KIND_META[comment.kind] ?? KIND_META.suggestion
                  const Icon = meta.icon
                  return (
                    <div
                      key={comment.id}
                      className="flex flex-col gap-1 rounded-lg border bg-muted/30 p-3"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            'flex size-5 items-center justify-center rounded',
                            meta.chip,
                          )}
                        >
                          <Icon className="size-3" aria-hidden="true" />
                        </span>
                        <span className="text-xs font-medium">
                          {meta.label}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {comment.targetLabel}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed">{comment.body}</p>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </section>
  )
}
